import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { GlassCard } from '../components/GlassCard';
import { List, RefreshCw } from 'lucide-react';
function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; }
}

const RANGE_PRESETS = [
  { key: 'this_month', label: 'This Month' },
  { key: 'last_30', label: 'Last 30d' },
  { key: 'qtd', label: 'QTD' },
  { key: 'ytd', label: 'YTD' },
  { key: 'custom', label: 'Custom' },
];

function getDateRange(key) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  switch (key) {
    case 'this_month': return { from: `${y}-${String(m + 1).padStart(2, '0')}-01`, to: '' };
    case 'last_30': {
      const f = new Date(now); f.setDate(d - 30);
      return { from: f.toISOString().slice(0, 10), to: '' };
    }
    case 'qtd': {
      const qm = m - (m % 3);
      return { from: `${y}-${String(qm + 1).padStart(2, '0')}-01`, to: '' };
    }
    case 'ytd': return { from: `${y}-01-01`, to: '' };
    default: return { from: '', to: '' };
  }
}

const SOURCE_COLORS = {
  sale: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  expense: 'bg-red-500/15 text-red-400 border-red-500/25',
  purchase: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  journal: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
};

export default function ErpLedger() {
  const { token } = useAuth();
  const toast = useToast();
  const { formatMoney, currencySymbol } = useTenantFormat();

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [rangeKey, setRangeKey] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState('');

  const loadAccounts = useCallback(async () => {
    if (!token) return;
    try {
      const r = await api.erpLedgerAccounts(token);
      setAccounts(r?.accounts || []);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const loadLedger = useCallback(async () => {
    if (!token || !selectedAccount) return;
    setLoading(true);
    const range = rangeKey === 'custom' ? { from: customFrom, to: customTo } : getDateRange(rangeKey);
    try {
      const r = await api.erpLedger(token, { account_name: selectedAccount, from: range.from, to: range.to, limit: 200 });
      setData(r);
    } catch (e) {
      if (e.status === 403) return;
      toast.show(e.message || 'Failed to load ledger', 'error');
    } finally { setLoading(false); }
  }, [token, selectedAccount, rangeKey, customFrom, customTo, toast]);

  useEffect(() => { loadLedger(); }, [loadLedger]);

  const filteredAccounts = accounts.filter(a =>
    a.account_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-5" data-testid="erp-ledger-page">
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Account picker */}
        <div className="relative" data-testid="erp-ledger-account-picker">
          <button
            onClick={() => setShowDropdown(v => !v)}
            className="h-10 px-4 rounded-xl bg-white/[0.05] border border-white/10 text-white text-sm font-semibold min-w-[200px] text-left flex items-center justify-between gap-2 hover:border-white/20 transition-colors"
          >
            <span className={selectedAccount ? 'text-white' : 'text-white/30'}>{selectedAccount || 'Pick an account…'}</span>
            <List size={14} className="text-white/40" />
          </button>
          {showDropdown && (
            <div className="absolute z-30 top-full left-0 mt-1 w-72 bg-[#0B1220] border border-white/15 rounded-xl shadow-2xl max-h-64 overflow-hidden">
              <div className="p-2 border-b border-white/10">
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search accounts..."
                  className="w-full h-8 px-3 rounded-lg bg-white/[0.05] border border-white/10 text-white text-xs placeholder:text-white/30 focus:outline-none"
                  autoFocus
                />
              </div>
              <div className="overflow-y-auto max-h-48">
                {filteredAccounts.map(a => (
                  <button
                    key={a.account_name}
                    onClick={() => { setSelectedAccount(a.account_name); setShowDropdown(false); setSearch(''); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-white/10 transition-colors flex items-center justify-between ${selectedAccount === a.account_name ? 'bg-[#2563EB]/10 text-[#2563EB]' : 'text-white/80'}`}
                  >
                    <span>{a.account_name}</span>
                    <span className="text-white/30 text-[10px] uppercase">{a.account_type}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Date range pills */}
        <div className="flex gap-1">
          {RANGE_PRESETS.map(r => (
            <button
              key={r.key}
              onClick={() => setRangeKey(r.key)}
              data-testid={`erp-ledger-range-pill-${r.key}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${rangeKey === r.key ? 'bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/25' : 'text-white/40 hover:text-white/60'}`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Custom date inputs */}
        {rangeKey === 'custom' && (
          <div className="flex gap-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="h-9 px-2 rounded-lg bg-white/[0.05] border border-white/10 text-white text-xs focus:outline-none" />
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="h-9 px-2 rounded-lg bg-white/[0.05] border border-white/10 text-white text-xs focus:outline-none" />
          </div>
        )}

        {/* Refresh */}
        <button onClick={loadLedger} className="p-2 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors" data-testid="erp-ledger-refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      {!selectedAccount ? (
        <GlassCard dark className="p-12 text-center">
          <List size={32} className="text-white/15 mx-auto mb-3" />
          <p className="text-white/40 text-sm">Pick an account to see its history</p>
        </GlassCard>
      ) : loading ? (
        <div className="py-12 flex justify-center">
          <div className="w-6 h-6 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* Summary strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard label="Opening Balance" value={data.opening_balance} formatMoney={formatMoney} />
            <KpiCard label="Total Debits" value={data.total_debits} color="#10B981" formatMoney={formatMoney} />
            <KpiCard label="Total Credits" value={data.total_credits} color="#EF4444" formatMoney={formatMoney} />
            <KpiCard label="Closing Balance" value={data.closing_balance} color="#2563EB" formatMoney={formatMoney} />
          </div>

          {/* Entries table */}
          <GlassCard dark className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="erp-ledger-table">
                <thead>
                  <tr className="bg-white/[0.04] border-b border-white/10">
                    <th className="px-5 py-3 text-left text-white/50 text-xs font-bold uppercase tracking-wider">Date</th>
                    <th className="px-5 py-3 text-left text-white/50 text-xs font-bold uppercase tracking-wider">Source</th>
                    <th className="px-5 py-3 text-left text-white/50 text-xs font-bold uppercase tracking-wider">Description</th>
                    <th className="px-5 py-3 text-right text-white/50 text-xs font-bold uppercase tracking-wider">Debit ₹</th>
                    <th className="px-5 py-3 text-right text-white/50 text-xs font-bold uppercase tracking-wider">Credit ₹</th>
                    <th className="px-5 py-3 text-right text-white/50 text-xs font-bold uppercase tracking-wider">Running Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.entries || []).map((e, i) => (
                    <tr key={i} data-testid={`erp-ledger-row-${i}`} className={`border-b border-white/[0.04] hover:bg-white/[0.04] ${i % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                      <td className="px-5 py-3 text-white/60 text-xs tabular-nums whitespace-nowrap">{fmtDate(e.date)}</td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${SOURCE_COLORS[e.source_type] || 'bg-white/5 text-white/40 border-white/10'}`}>
                          {e.source_type}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-white/80 text-xs max-w-xs truncate">{e.description}</td>
                      <td className="px-5 py-3 text-right text-emerald-400 tabular-nums text-xs font-bold">{e.debit_rupees > 0 ? formatMoney(e.debit_rupees) : '—'}</td>
                      <td className="px-5 py-3 text-right text-red-400 tabular-nums text-xs font-bold">{e.credit_rupees > 0 ? formatMoney(e.credit_rupees) : '—'}</td>
                      <td className="px-5 py-3 text-right text-white font-bold tabular-nums text-xs">{formatMoney(e.running_balance)}</td>
                    </tr>
                  ))}
                  {(!data.entries || data.entries.length === 0) && (
                    <tr><td colSpan={6} className="py-12 text-center text-white/40 text-sm">No entries for this period</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </GlassCard>
        </>
      ) : null}
    </div>
  );
}

function KpiCard({ label, value, color, formatMoney }) {
  return (
    <GlassCard dark className="p-4">
      <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">{label}</p>
      <p className="text-white font-extrabold text-lg tabular-nums mt-1" style={color ? { color } : undefined}>{formatMoney(value)}</p>
    </GlassCard>
  );
}


